// В.2.d-3: keyword `posthumous_contract` (Посмертный контракт) — общий
// keyword. Используется Оцифровкой души (наложение через Действие) и в
// будущем Экспериментом #42 (epic, base.keywords содержит этот keyword).
//
// Поведение (диздок 6.7):
//   - При смерти юнита с этим keyword'ом он сначала уходит в сброс через
//     стандартный путь смерти (Посмертия и on_unit_died-триггеры
//     срабатывают). После этого вызывается resurrectUnit на случайную
//     свободную ячейку СВОЕЙ стороны (т.е. стороны owner-владельца).
//   - Воскрешение по правилам resurrectUnit: 50% max ХП, max_armor как у
//     свежей карты, без Initiation Cooldown.
//   - Контракт ОДНОРАЗОВЫЙ. После первого срабатывания — keyword снимается
//     с воскрешённой карты (через убирание из card.keywords и
//     card.persistentKeywords). Флаг card.posthumousContractUsed = true
//     для трассировки.
//   - Если у владельца нет свободной ячейки — резурекция не состоялась,
//     контракт ОСТАЁТСЯ (флаг не выставляется): при следующей смерти
//     попробует ещё раз. Это намеренное решение — карта-цель не теряет
//     контракт зря.
//   - Молчание цели снимает keyword (как любой текстовый эффект через
//     applySilence: persistentKeywords/keywords обнуляются).
//
// API:
//   applyPosthumousContract(state, target, sourceId, sourceLabel?)
//     — навешивает keyword через addPermanentKeyword.
//   isPosthumousContract(card) — проверка hasKeyword 'deathrattle_contract'.
//   triggerPosthumousContractOnDeath(state, deadCard, ownerIdx)
//     — вызывается из обработчика смерти ПОСЛЕ обычных deathrattles.
//       Возвращает true, если воскрешение состоялось.
//
// Интеграция в путь смерти — в src/engine/abilities/livlife/posthumous_contract.js
// через registerOnUnitDiedExtraHandler.

import { hasKeyword, removeKeyword, KW } from '../keywords.js';
import { addPermanentKeyword } from './permanent.js';
import { resurrectUnit } from '../abilities/livlife/resurrect.js';

const KW_NAME = KW.POSTHUMOUS_CONTRACT;

export function applyPosthumousContract(state, target, sourceId = 'unknown', sourceLabel = '') {
  if (!target) return false;
  if (target.type !== 'unit') return false;
  return addPermanentKeyword(state, target, KW_NAME, sourceId, sourceLabel);
}

export function isPosthumousContract(card) {
  if (!card) return false;
  if (card.posthumousContractUsed) return false;
  return hasKeyword(card, KW_NAME);
}

// Вызывается из обработчика смерти (см. abilities/livlife/posthumous_contract.js).
// deadCard уже в стопке сброса owner. ownerIdx — сторона владельца.
// Возвращает true, если воскрешение прошло.
export function triggerPosthumousContractOnDeath(state, deadCard, ownerIdx) {
  if (!deadCard) return false;
  if (deadCard.posthumousContractUsed) return false;
  if (!hasKeyword(deadCard, KW_NAME)) return false;
  // Молчание блокирует (но Молчание уже могло снять keyword через
  // applySilence — тогда hasKeyword вернёт false выше). Защитная проверка.
  if (deadCard.current?.silenced) return false;
  if (deadCard.silenceSources && deadCard.silenceSources.size > 0) return false;

  const owner = state.players?.[ownerIdx];
  if (!owner) return false;
  // Если нет свободной ячейки — контракт НЕ срабатывает, флаг НЕ ставится.
  if (!owner.hasFreeSlot()) {
    state.log?.add(
      `🔁 ${deadCard.name_ru || deadCard.id} [${owner.name}]: Посмертный контракт — нет свободной ячейки, контракт остаётся`
    );
    return false;
  }
  // resurrectUnit извлечёт карту из сброса.
  const ok = resurrectUnit(state, deadCard, ownerIdx, null);
  if (!ok) return false;
  // Снять keyword: используется одноразово.
  deadCard.posthumousContractUsed = true;
  if (Array.isArray(deadCard.persistentKeywords)) {
    deadCard.persistentKeywords = deadCard.persistentKeywords.filter(k => k !== KW_NAME);
  }
  removeKeyword(deadCard, KW_NAME);
  state.log?.add(
    `🔁 ${deadCard.name_ru || deadCard.id} [${owner.name}]: Посмертный контракт — воскрешение`
  );
  return true;
}
